///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthBomReportService } from './bom-report.service';
import { BomSelectionFieldTypeEnum } from './bom-selection-field-type.enum';
import { Subscription } from 'rxjs';
import { GtthReportsTabs, REPORT_TABS } from '../reports/reports-tabs.enum';
import { FeatureTypeEnum, GtthKeyValueDTO, ProductAccessoryTypeEnum } from '../../../generated/shared-types.model';
import { ExtendedBomDeviceDTO } from './dto/extended-bom-device-dto.model';
import { AbstractInteractiveWindow } from '../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../project-interactive-windows-popup.service';
import { GtthExportService } from '../../../services/project/export.service';
import { PARAM_BBOX } from '../../../shared/constants/url-params.constants';
import { ActivatedRoute } from '@angular/router';
import { GtthProjectInfoViewService } from '../../../layouts/navbar/project-info-view.service';
import { E2eTestUtils } from '../../../../testing/e2e-test-utils';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ProjectProductsService } from '../../logical-topology/engine/project-products-service';
import { GtthChangeLinkNodesDataCommand } from '../../logical-topology/engine/commands/link/change-link-nodes-data-command';
import { FormControl, FormsModule, NgModel } from '@angular/forms';
import { naturalCompare } from '../../../shared/utils';
import { SuiSvgIconComponent, SuiTooltipDirective } from '@siklu/sui-lib';
import { TabsNavComponent } from '../../../shared/tabs-nav/tabs-nav.component';
import { ContextSensitiveHelpComponent } from '../../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { NgClass } from '@angular/common';
import { GtthNodePointerComponent } from '../map-search-pointers/node-pointer.component';
import { GtthValidationCustomModule } from '../../../shared/directives/validation-custom/validation-custom.module';
import { GtthProjectsListModule } from '../../../projects/projects.module';
import { FeetConvert } from '../../../shared/pipes/pipe.FeetConvert';
import { GtthSelectionFieldComponent } from './bom-selection-field.component';
import { GtthBomDeviceSettingsComponent } from './bom-device-settings.component';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';
import { ActionIconDirectivesModule } from '../../../shared/directives/action-icon-directives/action-icon-directives.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { GtthNumberInputValidationDirective } from '../../../shared/directives/validation-directives/number-input.directive';

@Component({
    selector: 'winde-project-gtth-bom-report',
    templateUrl: './bom-report.component.html',
    styleUrls: ['./bom-report.scss', '../project-windows.scss'],
    imports: [
        FormsModule,
        DragDropModule,
        MatDialogModule,
        NgClass,
        SuiSvgIconComponent,
        SuiTooltipDirective,
        TabsNavComponent,
        ContextSensitiveHelpComponent,
        GtthNodePointerComponent,
        GtthSelectionFieldComponent,
        GtthBomDeviceSettingsComponent,
        FeetConvert,
        GtthProjectsListModule,
        GtthValidationCustomModule,
        ButtonsDirectivesModule,
        ActionIconDirectivesModule,
        GtthNumberInputValidationDirective
    ]
})
export class GtthBomReportWindowComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    @ViewChild('reportContent') reportContentContainer: ElementRef;

    @ViewChildren(NgModel) ngModelList: QueryList<NgModel>;

    public readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();

    public accessoryType = ProductAccessoryTypeEnum;
    public fieldType = BomSelectionFieldTypeEnum;
    public readonly reportsTabValues = REPORT_TABS;
    public reportsTabs = GtthReportsTabs;
    public nodesNames: string[];
    public activeNodeIndex: number;
    public activeNodeId: number;
    public activeNodeDevices: ExtendedBomDeviceDTO[];
    public activeNodeName: string;
    public activeNodeHeight: number;
    public activeNodeLatitude: number;
    public activeNodeLongitude: number;
    public showNodesList: boolean;
    public showGeneralConfig = false;
    public currentTab = GtthReportsTabs.ALL;
    public activeLayerIndex: number;
    public layersObject: Record<number, string>;
    private layerChangedSubscriber: Subscription;
    private linksStateSubscription: Subscription;
    private modifyLinkNodesDataCommand: GtthChangeLinkNodesDataCommand;

    constructor(
        private readonly elementRef: ElementRef,
        public dialogRef: MatDialogRef<GtthBomReportWindowComponent>,
        public bomReportService: GtthBomReportService,
        public historyService: GtthHistoryService,
        private readonly exportService: GtthExportService,
        private readonly projectInfoService: GtthProjectInfoViewService,
        private readonly projectProductsService: ProjectProductsService
    ) {
        super(elementRef);

        this.layersObject = projectInfoService.getLayersAsKeyValueObj();
        this.setActiveLayerIndex(this.historyService.logicalTopology.activeLayerIdx);

        this.linksStateSubscription = this.bomReportService.subscribeToLinksChanges().subscribe(() => {
            this.loadData();
        });

        this.layerChangedSubscriber = this.projectInfoService.layerChangedSubscription().subscribe((layerIndex: number) => {
            this.layersObject = projectInfoService.getLayersAsKeyValueObj();
            this.setActiveLayerIndex(layerIndex);
            this.loadData();
        });
    }

    setNodeDevicesData(): void {
        this.activeNodeDevices.forEach((nodeDevice: ExtendedBomDeviceDTO) => {
            let deviceAssignedName = '';
            let deviceIP = '';

            nodeDevice.getNeighboursNames().forEach((neighborName: string) => {
                const activeLink = this.historyService.logicalTopology.getLinkByNodeNames(this.activeNodeName, neighborName);
                if (activeLink) {
                    const isActiveNodeLinkInitiator = this.activeNodeId === activeLink.getFromId();
                    let currentLinkAssignedName;
                    let currentLinkIP;

                    if (isActiveNodeLinkInitiator) {
                        currentLinkAssignedName = activeLink.getAssignedName0();
                        currentLinkIP = activeLink.getNode0IPAddress();
                    } else {
                        currentLinkAssignedName = activeLink.getAssignedName1();
                        currentLinkIP = activeLink.getNode1IPAddress();
                    }
                    if (!deviceAssignedName && currentLinkAssignedName) {
                        deviceAssignedName = currentLinkAssignedName;
                    }
                    if (!deviceIP && currentLinkIP) {
                        deviceIP = currentLinkIP;
                    }
                }
            });
        });
    }

    ngOnInit() {
        this.loadData();
    }

    ngOnDestroy() {
        if (this.historyService.isEditModeEnabled) {
            if (this.modifyLinkNodesDataCommand) {
                this.historyService.doCommand(this.modifyLinkNodesDataCommand).then(() => {
                    this.triggerModifiedLinksState();
                });
            } else {
                this.bomReportService.saveBom(true);
            }
        }
        this.nodesNames = undefined;
        if (this.linksStateSubscription) {
            this.linksStateSubscription.unsubscribe();
            this.linksStateSubscription = null;
        }
        if (this.layerChangedSubscriber) {
            this.layerChangedSubscriber.unsubscribe();
            this.layerChangedSubscriber = null;
        }
    }

    clear() {
        this.dialogRef.close('cancel');
    }

    triggerModifiedLinksState(): void {
        const openInfoWindowConfig = this.historyService.projectUserSessionService.getSessionObject().infoWindowConfig;
        if (openInfoWindowConfig && openInfoWindowConfig.type === FeatureTypeEnum.LINK) {
            const isModifiedLinkInfoOpen = this.modifyLinkNodesDataCommand?.getIsLinkModified(openInfoWindowConfig.entityId);
            if (isModifiedLinkInfoOpen) {
                const link = this.historyService.logicalTopology.getLink(openInfoWindowConfig.entityId);
                this.bomReportService.triggerLinkState(link.getFromStation().getName(), link.getToStation().getName());
            }
        }
    }

    selectNode(nodeName, nodeIndex, scrollToTop = true, initialSelection = false) {
        this.activeNodeIndex = nodeIndex;
        const node = this.historyService.logicalTopology.getStationByName(nodeName);
        this.activeNodeName = node.getName();
        this.activeNodeId = node.getId();
        this.activeNodeHeight = node.getHeight();
        this.activeNodeLatitude = node.getPosition().latitude;
        this.activeNodeLongitude = node.getPosition().longitude;
        this.activeNodeDevices = this.bomReportService.getExtendedNodeDevices(this.activeLayerIndex, nodeName);
        this.setNodeDevicesData();
        if (scrollToTop) {
            this.scrollContainerToTop();
        }
    }

    switchNode(side: number) {
        if (this.activeNodeIndex === -1) {
            return;
        }

        this.activeNodeIndex += side;
        if (this.activeNodeIndex < 0) {
            this.activeNodeIndex = this.nodesNames.length - 1;
        } else if (this.activeNodeIndex >= this.nodesNames.length) {
            this.activeNodeIndex = 0;
        }

        this.selectNode(this.nodesNames[this.activeNodeIndex], this.activeNodeIndex);
    }

    changeDeviceRadio(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        this.bomReportService.setDeviceRadio(device, selectedValue, currentNodeName);
        this.loadData(false);
    }

    changeDeviceAntenna(device: ExtendedBomDeviceDTO, selectedValue: GtthKeyValueDTO, currentNodeName: string) {
        this.bomReportService.setDeviceAntenna(device, selectedValue, currentNodeName);
        this.loadData(false);
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthReportsTabs[tabIndex];
        this.loadData();
    }

    trackNodeName(_index, item) {
        return item.name;
    }

    public showDeviceConfig() {
        this.showGeneralConfig = !this.showGeneralConfig;
        setTimeout(() => {
            if (this.showGeneralConfig) {
                this.nodesNames = undefined;
            } else {
                this.loadData();
            }
        });
    }

    async exportBomReport() {
        if (this.isValid()) {
            await this.bomReportService.saveBom(true);
            this.exportService.exportBomConfigToExcel(this.projectInfoService.activeLayer);
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.clear();
        }
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    scrollContainerToTop() {
        if (this.reportContentContainer?.nativeElement) {
            this.reportContentContainer.nativeElement.scrollTop = 0;
        }
    }

    changeDeviceAssignedName(device: ExtendedBomDeviceDTO, newAssignedName: string, anControl: FormControl): void {
        anControl.updateValueAndValidity();

        if (anControl.errors?.error) {
            return;
        }

        if (!this.modifyLinkNodesDataCommand) {
            this.modifyLinkNodesDataCommand = this.historyService.command(GtthChangeLinkNodesDataCommand).create();
        }

        device.getNeighboursNames().forEach((neighbourName: string) => {
            const relatedLink = this.historyService.logicalTopology.getLinkByNodeNames(this.activeNodeName, neighbourName);
            this.modifyLinkNodesDataCommand.addLinkToCommand(relatedLink);

            if (this.activeNodeName === relatedLink.getFromStation().getName()) {
                this.modifyLinkNodesDataCommand.setNewAssignedNamesToLink(relatedLink, newAssignedName, undefined);
            } else {
                this.modifyLinkNodesDataCommand.setNewAssignedNamesToLink(relatedLink, undefined, newAssignedName);
            }
        });
    }

    changeDeviceIP(device: ExtendedBomDeviceDTO, newIpAddress: string, ipControl: FormControl): void {
        ipControl.updateValueAndValidity();

        if (ipControl.errors?.error) {
            return;
        }

        if (!this.modifyLinkNodesDataCommand) {
            this.modifyLinkNodesDataCommand = this.historyService.command(GtthChangeLinkNodesDataCommand).create();
        }

        device.getNeighboursNames().forEach((neighborName: string) => {
            const relatedLink = this.historyService.logicalTopology.getLinkByNodeNames(this.activeNodeName, neighborName);
            this.modifyLinkNodesDataCommand.addLinkToCommand(relatedLink);

            if (this.activeNodeName === relatedLink.getFromStation().getName()) {
                this.modifyLinkNodesDataCommand.setNewIpsToLink(relatedLink, newIpAddress, undefined);
            } else {
                this.modifyLinkNodesDataCommand.setNewIpsToLink(relatedLink, undefined, newIpAddress);
            }
        });
    }

    isMh(type: number): boolean {
        return this.projectProductsService.isMh(type);
    }

    isMhTg(type: number): boolean {
        return this.projectProductsService.isMhTgDevice(type);
    }

    isDeviceHasSectors(type: number): boolean {
        return this.projectProductsService.isDeviceHasSectors(type);
    }

    private setActiveLayerIndex(layerIndex: number): void {
        this.activeLayerIndex = layerIndex;
        if (!this.historyService.logicalTopology.layersModeEnabled) {
            this.activeLayerIndex = -1;
        }
    }

    private loadData(scrollContainerToTop = true): void {
        this.nodesNames = this.bomReportService.getBomNodeNames(this.activeLayerIndex, this.currentTab).sort(naturalCompare);

        if (!this.nodesNames || this.nodesNames.length === 0) {
            this.activeNodeIndex = -1;
            this.activeNodeName = undefined;
            this.activeNodeHeight = undefined;
            this.activeNodeLatitude = undefined;
            this.activeNodeLongitude = undefined;
            this.activeNodeDevices = undefined;
        } else {
            this.activeNodeIndex = this.nodesNames.findIndex((nameFromList) => nameFromList === this.activeNodeName);

            if (this.activeNodeIndex === -1) {
                this.activeNodeIndex = 0;
                this.activeNodeName = this.nodesNames[this.activeNodeIndex];
            }

            this.selectNode(this.activeNodeName, this.activeNodeIndex, scrollContainerToTop, true);
        }
    }

    private isValid(): boolean {
        return !this.ngModelList.some((ngModel: NgModel) => ngModel.control.invalid && ngModel.control.errors?.error);
    }
}

@Component({
    selector: 'winde-project-gtth-bom-report-popup',
    template: '',
    standalone: false
})
export class ProjectGtthBomReportPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthBomReportWindowComponent,
            ProjectInteractiveWindowsPopupService.BOM_REPORT,
            routerPath,
            bboxParams
        );
    }
}
